import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, Button } from '../components/ui';
import { designTokens } from '../utils/designTokens';
import toast from 'react-hot-toast';

const ContactCard = ({ icon, title, email, description, responseTime }) => (
  <Card>
    <div className={`p-${designTokens.spacing.cardPadding}`}>
      <div className="flex items-center mb-3">
        <span className="text-3xl mr-3">{icon}</span>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h3>
      </div>
      <p className="text-gray-600 dark:text-gray-400 mb-3">{description}</p>
      <a
        href={`mailto:${email}`}
        className="text-purple-600 dark:text-purple-400 hover:underline font-semibold"
      >
        {email}
      </a>
      <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
        Response time: {responseTime}
      </p>
    </div>
  </Card>
);

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: 'general',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // In production, this would send to an API endpoint
    console.log('Contact form submitted:', formData);
    toast.success('Message sent! We\'ll get back to you soon.');

    // Reset form
    setFormData({
      name: '',
      email: '',
      subject: '',
      category: 'general',
      message: ''
    });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-spooky-bg-darker">
      <Helmet>
        <title>Contact Us - Boop</title>
        <meta name="description" content="Get in touch with the Boop team for support, partnerships, or questions about the platform." />
      </Helmet>

      {/* Hero */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-900 dark:to-indigo-900 text-white">
        <div className={`max-w-7xl mx-auto px-${designTokens.spacing.pagePadding} py-12 text-center`}>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Get in Touch</h1>
          <p className="text-xl text-purple-100">
            We're here to help with any questions or concerns
          </p>
        </div>
      </div>

      <div className={`max-w-7xl mx-auto px-${designTokens.spacing.pagePadding} py-16`}>

        {/* Contact Methods */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Choose Your Contact Method
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            <ContactCard
              icon="🤝"
              title="General Support"
              email="support@boop.com"
              description="Questions about features, account issues, or general help"
              responseTime="24-48 hours"
            />

            <ContactCard
              icon="💰"
              title="Marketplace Issues"
              email="marketplace@boop.com"
              description="Buyer/seller disputes, payment problems, or payout questions"
              responseTime="12-24 hours"
            />

            <ContactCard
              icon="🚨"
              title="Report Abuse"
              email="abuse@boop.com"
              description="Content violations, harassment, fraud, or safety concerns"
              responseTime="6-12 hours"
            />

            <ContactCard
              icon="💼"
              title="Business Inquiries"
              email="hello@boop.com"
              description="Partnerships, media requests, API access, or collaborations"
              responseTime="2-3 business days"
            />

            <ContactCard
              icon="🔒"
              title="Privacy Concerns"
              email="privacy@boop.com"
              description="Data requests, GDPR/CCPA inquiries, or privacy questions"
              responseTime="48 hours"
            />

            <ContactCard
              icon="🐛"
              title="Technical Issues"
              email="bugs@boop.com"
              description="Platform errors, bugs, feature requests, or technical problems"
              responseTime="24-48 hours"
            />
          </div>
        </section>

        {/* Contact Form */}
        <section className="max-w-3xl mx-auto mb-16">
          <Card>
            <div className={`p-${designTokens.spacing.cardPadding}`}>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Send Us a Message</h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="general">General Support</option>
                    <option value="marketplace">Marketplace Issues</option>
                    <option value="abuse">Report Abuse</option>
                    <option value="business">Business Inquiry</option>
                    <option value="privacy">Privacy Concern</option>
                    <option value="technical">Technical Issue</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Brief description of your inquiry"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    placeholder="Please provide as much detail as possible..."
                  />
                </div>

                <Button type="submit" variant="primary" className="w-full">
                  Send Message
                </Button>

                <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  We typically respond within 24-48 hours during business days
                </p>
              </form>
            </div>
          </Card>
        </section>

        {/* Social & Community */}
        <section className="max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Join Our Community
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <a
              href="https://twitter.com/boopai"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Card>
                <div className={`p-${designTokens.spacing.cardPadding} text-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors`}>
                  <div className="text-4xl mb-3">🐦</div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">Twitter</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">@boopai</p>
                </div>
              </Card>
            </a>

            <a
              href="https://discord.gg/boop"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Card>
                <div className={`p-${designTokens.spacing.cardPadding} text-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors`}>
                  <div className="text-4xl mb-3">💬</div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">Discord</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Join Community</p>
                </div>
              </Card>
            </a>

            <a
              href="https://github.com/boop-ai"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Card>
                <div className={`p-${designTokens.spacing.cardPadding} text-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors`}>
                  <div className="text-4xl mb-3">💻</div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">GitHub</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Open Source</p>
                </div>
              </Card>
            </a>
          </div>
        </section>

        {/* FAQ & Docs Links */}
        <section className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Looking for Quick Answers?
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Check out our documentation and FAQ for instant help
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/docs"
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              View Documentation
            </a>
            <a
              href="/faq"
              className="px-6 py-3 bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 border-2 border-purple-600 dark:border-purple-400 rounded-lg font-semibold hover:bg-purple-50 dark:hover:bg-gray-600 transition-colors"
            >
              Read FAQ
            </a>
          </div>
        </section>

        {/* Status Page */}
        <section className="mt-12 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            Check our platform status and uptime at{' '}
            <a
              href="https://status.boop.com"
              className="text-purple-600 dark:text-purple-400 hover:underline font-semibold"
              target="_blank"
              rel="noopener noreferrer"
            >
              status.boop.com
            </a>
          </p>
        </section>
      </div>
    </div>
  );
};

export default Contact;
